import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { CopyButton } from "@/core/components/ui/copy-button";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { WalletDto } from "@/features/wallet/api/dto";
import { WalletRowActions } from "@/features/wallet/components/wallets/wallet-row-actions";
import {
  WALLET_OWNER_TYPES,
  WALLET_STATUSES,
  type WalletStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type WalletRow = WalletDto & { id: string };

/**
 * Fabrique les colonnes du tableau des portefeuilles. Le nom d'affichage du
 * propriétaire (`ownerName`) est renvoyé directement par wallet-service dans
 * `POST /Wallets/all` — plus besoin du lookup `/Parties/names` (le paramètre
 * `nameByPartyId` est conservé pour compat appelants mais ignoré).
 */
export const makeWalletsColumns = (
  _nameByPartyId?: ReadonlyMap<string, string>
): ColumnsClarFactory<WalletRow> => (navigate) =>
  walletsColumnsFactory(navigate);

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  WalletStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  PENDING_KYC: "warning",
  ACTIVE: "success",
  SUSPENDED: "destructive",
  CLOSED: "secondary",
};

/**
 * Colonnes de la liste des portefeuilles (`POST /Wallets/all`).
 * `WalletFilter` (Postman) : `ownerPartyId`, `ownerType`, `status` uniquement
 * — les autres colonnes n'ont pas de champ API, d'où `denied`.
 * Identifiants et références en `NoTranslate` (règle notranslate).
 */
export const walletsColumns: ColumnsClarFactory<WalletRow> = (navigate) =>
  walletsColumnsFactory(navigate);

const walletsColumnsFactory = (
  navigate: Parameters<ColumnsClarFactory<WalletRow>>[0]
): ReturnType<ColumnsClarFactory<WalletRow>> => [
  {
    id: "walletNumber",
    accessorKey: "walletNumber",
    header: t("wallet.wallets.columns.walletNumber"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    // Pas dans WalletFilter (Postman : ownerPartyId / ownerType / status).
    meta: { type: "denied" },
  },
  {
    id: "ownerType",
    accessorKey: "ownerType",
    header: t("wallet.wallets.columns.ownerType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(WALLET_OWNER_TYPES) },
  },
  {
    id: "ownerName",
    accessorKey: "ownerName",
    header: t("wallet.wallets.columns.ownerName"),
    cell: ({ getValue }) => {
      const name = (getValue() as string | null | undefined)?.trim();
      return name ? (
        <NoTranslate as="span" className="font-medium">
          {name}
        </NoTranslate>
      ) : (
        "—"
      );
    },
    // kyc-service n'indexe pas les noms (Postman v3.1) → filtre client-side
    // appliqué sur la page affichée uniquement par le parent (wallets.page.tsx).
    meta: {
      type: "text",
      placeholder: t("wallet.wallets.filters.ownerNamePlaceholder"),
    },
  },
  {
    id: "ownerPartyId",
    accessorKey: "ownerPartyId",
    header: t("wallet.wallets.columns.ownerPartyId"),
    cell: ({ getValue }) => {
      const ownerPartyId = (getValue() as string | undefined)?.trim();
      if (!ownerPartyId) return "—";
      return (
        <span className="inline-flex items-center gap-1">
          <NoTranslate as="span" className="font-mono text-xs text-muted-foreground">
            {ownerPartyId}
          </NoTranslate>
          <CopyButton value={ownerPartyId} />
        </span>
      );
    },
    meta: { type: "text" },
  },
  {
    id: "currency",
    accessorKey: "currency",
    header: t("wallet.wallets.columns.currency"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    // Pas dans WalletFilter.
    meta: { type: "denied" },
  },
  {
    id: "kycLevel",
    accessorKey: "kycLevel",
    header: t("wallet.wallets.columns.kycLevel"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{(getValue() as string) || "—"}</Badge>
      </NoTranslate>
    ),
    // Pas dans WalletFilter.
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("wallet.wallets.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as WalletStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(WALLET_STATUSES) },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <WalletRowActions wallet={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
